// Editor.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useEditorCESDK } from '../hooks/useEditorCESDK';
import supabase from '../utils/supabaseUtils';
import './Editor.css';
import TemplateBrowser from './TemplateBrowser';
import stackedLogo from '../assets/playmaker-logo-stacked-w-tag.png'; 
import AppNavigation from './AppNavigation';
import { 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem, 
  MenuGroup, 
  IconButton, 
  Tooltip,
  MenuDivider
} from '@chakra-ui/react';
import { 
  AddIcon, 
  ViewIcon, 
  CheckIcon, 
  ChevronDownIcon, 
  EditIcon 
} from '@chakra-ui/icons';
import { 
  FaSave, 
  FaHome, 
  FaLayerGroup, 
  FaFilter 
} from 'react-icons/fa';

function Editor() {
  const editorContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [templateName, setTemplateName] = useState("Untitled Template");
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [user, setUser] = useState(null);
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const editorInitializedRef = useRef(false);
  
  // Category and subcategory state
  const [styleCategory, setStyleCategory] = useState('Sports');
  const [styleSubcategory, setStyleSubcategory] = useState('');
  const [otherSubcategory, setOtherSubcategory] = useState('');

  const [isAdmin, setIsAdmin] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    cesdk,
    status,
    error,
    initialize,
    createBlankScene,
    registerAssetSources,
    loadTemplates,
    configureUI,
    loadSceneFromSupabase,
    saveSceneToSupabase,
    getPlaceholders,
    configureImageUpload,
    cleanup,
    isReady
  } = useEditorCESDK();

  // Define categories and subcategories
  const categories = [
    { value: 'Sports', label: 'Sports' },
    { value: 'Corporate', label: 'Corporate' },
    { value: 'Other', label: 'Other' }
  ];

  const subcategories = {
    Sports: [
      'Cheer', 'Dance', 'Ice Hockey', 'Equestrian', 'Gymnastics', 'Lacrosse',
      'Tennis', 'Soccer', 'Baseball/Softball', 'Basketball', 'Wrestling', 'Football', 'Other'
    ],
    Corporate: ['Staff Profile', 'Artistic', 'Branded'],
    Other: ['Custom']
  };

  // Create a global function to update template state from the dock
  useEffect(() => {
    window.updateTemplateState = (templateId, templateName) => {
      setCurrentTemplateId(templateId);
      setTemplateName(templateName || "Untitled Template");
      setSaveMessage("Template loaded successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    };
    
    return () => {
      window.updateTemplateState = undefined;
    };
  }, []);

  // Log when key state changes
  useEffect(() => {
    console.log(`Editor status changed: ${status}, isReady: ${isReady}`);
  }, [status, isReady]);

  useEffect(() => {
    console.log(`Current template ID: ${currentTemplateId}`);
  }, [currentTemplateId]);

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (authError || !session) {
          navigate('/login');
          return;
        }
        
        setUser(session.user);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      }
    };

    checkAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setUser(session.user);
      } else {
        setUser(null);
        navigate('/login');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  // Add this effect to check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('app_users')
          .select('is_admin')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        setIsAdmin(data?.is_admin || false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);

  // Initialize CESDK
  useEffect(() => {
    if (!user || !editorContainerRef.current || editorInitializedRef.current) {
      return;
    }

    const initEditor = async () => {
      editorInitializedRef.current = true;
      setIsLoading(true);
      
      try {
        console.log("Starting editor initialization...");
        
        // Step 1: Create the editor
        const instance = await initialize(editorContainerRef.current, { userId: user.id });
        
        // Step 2: Register asset sources
        await registerAssetSources();
        
        // Step 3: Load templates
        const fetchedTemplates = await loadTemplates();
        setTemplates(fetchedTemplates);
        
        // Step 4: Configure UI
        await configureUI();
        
        // Step 5: Configure image upload (new functionality)
        await configureImageUpload(user.id);
        
        // Step 6: Create a blank scene
        await createBlankScene();
        
       
        console.log("Editor initialization complete");
      } catch (error) {
        console.error('Editor initialization failed:', error);
        setSaveMessage('Failed to initialize editor: ' + (error.message || 'Unknown error'));
        editorInitializedRef.current = false; // Allow retry on failure
      } finally {
        setIsLoading(false);
      }
    };

    initEditor();
    
    return () => {
      // No need to call cleanup here as it's handled by the hook's useEffect
    };
  }, [user, initialize, registerAssetSources, loadTemplates, configureUI, createBlankScene, configureImageUpload]);

  useEffect(() => {
    // Create a global handler for template selection
    window.handleTemplateSelection = async (templateId) => {
      if (!isReady) {
        console.warn("Editor not ready for template selection");
        return;
      }
      
      setIsLoading(true);
      setSaveMessage("Loading template...");
      
      try {
        const template = await loadSceneFromSupabase(templateId);
        
        setCurrentTemplateId(templateId);
        setTemplateName(template.name || "Untitled Template");
        setIsPublic(template.is_public || false);
        
        // Update style category and subcategory if available
        if (template.styleCategory) {
          setStyleCategory(template.styleCategory);
          
          if (template.styleCategory === 'Other') {
            setOtherSubcategory(template.styleSubcategory || '');
            setStyleSubcategory('');
          } else {
            setStyleSubcategory(template.styleSubcategory || '');
            setOtherSubcategory('');
          }
        }
        
        setSaveMessage("Template loaded successfully!");
        setTimeout(() => setSaveMessage(""), 3000);
      } catch (error) {
        console.error("Error loading template:", error);
        setSaveMessage(`Error loading template: ${error.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    return () => {
      window.handleTemplateSelection = undefined;
    };
  }, [isReady, loadSceneFromSupabase]);
  
  // Update the handleSave function
  const handleSave = async () => {
    if (!cesdk || !isReady || !user) {
      console.error("CESDK not properly initialized or not ready");
      setSaveMessage("Editor not ready. Please refresh the page.");
      return;
    }
    
    setIsSaving(true);
    setSaveMessage("Saving template...");
    
    try {
      // Get the actual subcategory value
      const effectiveSubcategory = styleCategory === 'Other' ? otherSubcategory : styleSubcategory;
      
      // Check if this is an existing template
      if (currentTemplateId) {
        // Fetch the template to check ownership and public status
        const { data: template, error: fetchError } = await supabase
          .from('templates')
          .select('user_id, is_public')
          .eq('id', currentTemplateId)
          .single();
          
        if (fetchError) throw fetchError;
        
        // If user is not the owner and template is public, create a new private template
        if (template.user_id !== user.id && template.is_public) {
          const savedTemplate = await saveSceneToSupabase(
            templateName,
            user.id,
            styleCategory,
            effectiveSubcategory,
            null, // Create new template
            false  // Always private for non-owners
          );
          
          setCurrentTemplateId(savedTemplate.id);
          setSaveMessage("Template saved as private copy!");
        } 
        // If user is the owner, update the existing template
        else if (template.user_id === user.id) {
          const savedTemplate = await saveSceneToSupabase(
            templateName,
            user.id,
            styleCategory,
            effectiveSubcategory,
            currentTemplateId,
            isPublic && isAdmin // Only allow public if user is admin
          );
          
          setCurrentTemplateId(savedTemplate.id);
          setSaveMessage("Template updated successfully!");
        }
        // If user is not the owner and template is private, show error
        else {
          throw new Error("You don't have permission to edit this template");
        }
      } 
      // Creating a new template
      else {
        const savedTemplate = await saveSceneToSupabase(
          templateName,
          user.id,
          styleCategory,
          effectiveSubcategory,
          null,
          isPublic && isAdmin // Only allow public if user is admin
        );
        
        setCurrentTemplateId(savedTemplate.id);
        setSaveMessage("New template created successfully!");
      }
      
      // Update templates list
      const updatedTemplates = await loadTemplates();
      setTemplates(updatedTemplates);
      
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error saving template:", error);
      setSaveMessage(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Create a new blank template
  const createNewTemplate = async () => {
    if (!isReady) {
      setSaveMessage("Editor not ready yet. Please wait...");
      return;
    }
    
    setIsLoading(true);
    setSaveMessage("Creating new template...");
    
    try {
      console.log("Starting New Template creation");
      
      // First completely dispose the current instance
      if (cesdk) {
        try {
          console.log("Disposing current CESDK instance");
          cleanup(); // Use the cleanup function from the hook
        } catch (e) {
          console.warn("Error cleaning up CESDK:", e);
        }
      }
      
      // Wait longer to ensure complete cleanup
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a new instance
      const container = editorContainerRef.current;
      if (!container) {
        throw new Error("Editor container not found");
      }
      
      // Initialize a fresh editor - use a clean configuration
      const instance = await initialize(container, { userId: user.id });
      
      console.log("New editor instance created", instance);
      
      // Wait longer for the engine to be fully initialized
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify that the engine is properly initialized
      if (!instance || !instance.engine || !instance.engine.scene) {
        throw new Error("CESDK engine not properly initialized. Missing scene API.");
      }
      
      console.log("Engine structure:", {
        hasEngine: !!instance.engine,
        hasSceneAPI: !!instance.engine?.scene,
        hasBlockAPI: !!instance.engine?.block,
        hasAssetAPI: !!instance.engine?.asset
      });
      
      // Setup the editor components following the same sequence as initEditor
      console.log("Setting up editor components");
      
      // First create a scene if needed
      if (instance.engine.scene && instance.engine.scene.create) {
        const newScene = await instance.engine.scene.create();
        console.log("Created new scene:", newScene);
      }
      
      // Now the asset API should be available
      await registerAssetSources();
      
      // Load templates
      await loadTemplates();
      
      // Configure UI
      await configureUI();
      
      // Configure image upload (new functionality)
      await configureImageUpload(user.id);
      
      // Create blank scene
      console.log("Creating blank scene");
      await createBlankScene();
      
      // Reset template info
      setTemplateName("Untitled Template");
      setCurrentTemplateId(null);
      setStyleCategory('Sports');
      setStyleSubcategory('');
      setOtherSubcategory('');
      
      setSaveMessage("New template created!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error creating new template:", error);
      setSaveMessage(`Error: ${error.message || 'Unknown error'}`);
      
      // Try to recover by reinitializing the editor
      try {
        if (editorContainerRef.current) {
          const freshInstance = await initialize(editorContainerRef.current);
          console.log("Recovered by creating fresh editor instance");
          setSaveMessage("Recovered from error. The editor has been reset.");
        }
      } catch (recoveryError) {
        console.error("Failed to recover:", recoveryError);
      }
      
      setTimeout(() => setSaveMessage(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Load a template when selected
  const handleSelectTemplate = async (templateId) => {
    if (!isReady) {
      setSaveMessage("Editor not ready yet. Please wait...");
      return;
    }
    
    console.log(`Template selected - ID: ${templateId}`);
    setIsLoading(true);
    setSaveMessage("Loading template...");
    
    try {
      // Load the template
      console.log(`Loading template ID: ${templateId}...`);
      const template = await loadSceneFromSupabase(templateId);
      
      // Update state with loaded template info
      setCurrentTemplateId(templateId);
      setTemplateName(template.name || "Untitled Template");
      
      // Update style category and subcategory if available
      if (template.styleCategory) {
        setStyleCategory(template.styleCategory);
        
        if (template.styleCategory === 'Other') {
          setOtherSubcategory(template.styleSubcategory || '');
          setStyleSubcategory('');
        } else {
          setStyleSubcategory(template.styleSubcategory || '');
          setOtherSubcategory('');
        }
      }
      
      setSaveMessage("Template loaded successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error loading template:", error);
      setSaveMessage(`Error loading template: ${error.message || 'Unknown error'}`);
      setTimeout(() => setSaveMessage(""), 5000);
    } finally {
      setShowTemplateBrowser(false);
      setIsLoading(false);
    }
  };

  // Test placeholders in the current scene
  const testPlaceholders = () => {
    if (!cesdk || !isReady) {
      setSaveMessage("Editor not ready. Please wait...");
      return;
    }
    
    try {
      const placeholders = getPlaceholders();
      
      if (placeholders.length > 0) {
        setSaveMessage(`Found ${placeholders.length} placeholders: ${placeholders.map(p => p.name).join(', ')}`);
      } else {
        setSaveMessage("No placeholders found in the scene");
      }
      
      setTimeout(() => setSaveMessage(""), 5000);
    } catch (error) {
      console.error("Error testing placeholders:", error);
      setSaveMessage(`Error testing placeholders: ${error.message || 'Unknown error'}`);
      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  // Handle category and subcategory selection combined
  const handleCategorySelect = (category, subcategory) => {
    setStyleCategory(category);
    
    if (category === 'Other' && subcategory === 'Custom') {
      setOtherSubcategory('');
      setStyleSubcategory('');
    } else {
      setStyleSubcategory(subcategory);
      setOtherSubcategory('');
    }
  };

  // Set the body data attribute for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-page', 'editor');
    
    return () => {
      document.body.removeAttribute('data-page');
    }
  }, []);

  if (error) {
    return <div className="error-message">Error initializing editor: {error.message || 'Unknown error'}</div>;
  }

  return (
    <div className="editor-page">
      {/* Add the shared App Navigation component */}
      {user && <AppNavigation user={user} isEditor={true} />}
      
      <div className="editor-header">
        <div className="logo">PlayMaker Pics</div>
        <div className="template-controls">
          <Tooltip label="Browse Templates" placement="bottom">
            <IconButton
              icon={<ViewIcon />}
              aria-label="Browse Templates"
              onClick={() => setShowTemplateBrowser(true)}
              isDisabled={!isReady}
              className="icon-button"
              size="md"
              colorScheme="blue"
              variant="ghost"
              mr={2}
            />
          </Tooltip>
          
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template Name"
            className="template-name-input"
            disabled={!isReady}
            style={{ marginRight: '10px', width: '200px' }}
          />
          
          <div className="template-public-option">
            <label className="public-label">
              <input 
                type="checkbox" 
                checked={isPublic} 
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={!isReady || !isAdmin} 
              />
              <span>
                {isAdmin ? 'Public' : 'Public (Admin Only)'}
                {!isAdmin && <Tooltip label="Only administrators can create public templates">ℹ️</Tooltip>}
              </span>
            </label>
          </div>
          
          <Menu>
            <Tooltip label="Category & Type" placement="bottom">
              <MenuButton
                as={IconButton}
                aria-label="Select Category"
                icon={<FaFilter />}
                isDisabled={!isReady}
                className="icon-button"
                size="md"
                colorScheme="teal"
                variant="ghost"
                mr={2}
              />
            </Tooltip>
            <MenuList>
              {categories.map(cat => (
                <React.Fragment key={cat.value}>
                  <MenuGroup title={cat.label}>
                    {cat.value !== 'Other' ? (
                      subcategories[cat.value].map(sub => (
                        <MenuItem 
                          key={`${cat.value}-${sub}`}
                          onClick={() => handleCategorySelect(cat.value, sub)}
                          icon={
                            styleCategory === cat.value && styleSubcategory === sub ? 
                            <CheckIcon /> : undefined
                          }
                        >
                          {sub}
                        </MenuItem>
                      ))
                    ) : (
                      <>
                        <MenuItem 
                          onClick={() => handleCategorySelect('Other', 'Custom')}
                          icon={styleCategory === 'Other' ? <CheckIcon /> : undefined}
                        >
                          Custom
                        </MenuItem>
                        {styleCategory === 'Other' && (
                          <MenuItem closeOnSelect={false}>
                            <input
                              type="text"
                              value={otherSubcategory}
                              onChange={(e) => setOtherSubcategory(e.target.value)}
                              placeholder="Enter custom type"
                              style={{ width: '100%', border: '1px solid #ccc', padding: '4px' }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </MenuItem>
                        )}
                      </>
                    )}
                  </MenuGroup>
                  <MenuDivider />
                </React.Fragment>
              ))}
            </MenuList>
          </Menu>
          
          <Tooltip label="Save Template" placement="bottom">
            <IconButton
              icon={<FaSave />}
              aria-label="Save Template"
              onClick={handleSave}
              isDisabled={isSaving || !isReady}
              isLoading={isSaving}
              className="icon-button"
              size="md"
              colorScheme="green"
              variant="ghost"
              mr={2}
            />
          </Tooltip>
          
          <Tooltip label="New Template" placement="bottom">
            <IconButton
              icon={<AddIcon />}
              aria-label="New Template"
              onClick={createNewTemplate}
              isDisabled={!isReady}
              className="icon-button"
              size="md"
              colorScheme="purple"
              variant="ghost"
              mr={2}
            />
          </Tooltip>
          
          <Tooltip label="Dashboard" placement="bottom">
            <IconButton
              as={Link}
              to="/dashboard"
              icon={<FaHome />}
              aria-label="Dashboard"
              className="icon-button"
              size="md"
              colorScheme="red"
              variant="ghost"
            />
          </Tooltip>
        </div>
        {saveMessage && <div className="save-message">{saveMessage}</div>}
      </div>
      
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div>Loading Editor...</div>
        </div>
      )}
      
      <div 
        ref={editorContainerRef}
        className="editor-container" 
        style={{ 
          visibility: isLoading ? 'hidden' : 'visible',
          width: '100%',
          height: 'calc(100vh - 70px)'
        }}
      />
          
      {showTemplateBrowser && (
        <TemplateBrowser
          templates={templates}
          onSelectTemplate={handleSelectTemplate}
          onClose={() => setShowTemplateBrowser(false)}
        />
      )}
    </div>
  );
}

export default Editor;